<template>
   <Head>
      <title>{{ $t("meta.survey.title") }}</title>
      <meta name="description" :content="$t('meta.survey.content')" />
   </Head>

   <PageLoader :isLoading="state.isPageLoading" :loadErrors="loadErrors" data-cy="flowsel-page-container">
      <div class="text-regular flex h-full min-h-screen flex-col">
         <!-- Flowsel navigation bar after loading -->
         <FlowselNav :study="study" :flowName="flowName" :flowId="flowId" />

         <!-- Completed flow -->
         <!-- NB: 'flex-grow' is needed otherwise the section doesn't cover the whole page! -->
         <section v-if="isFlowComplete" class="flex h-full grow items-center bg-brand-dark text-center text-white">
            <div class="container max-w-lg">
               <h2 class="text-2xl lg:text-3xl">
                  {{ $t("survey.completed.heading") }}
               </h2>

               <p class="mt-8 text-xl lg:text-2xl">{{ $t("survey.completed.message") }}</p>
               <LinkButton url="/home" class="mt-8" :color="Theme.White">
                  {{ $t("redirects.go_to_home") }}
               </LinkButton>
            </div>
         </section>

         <!-- Flow has been updated & we need to ask this user to start again -->
         <MessageOnFlowUpdate
            v-else-if="state.showParticipationResetMessage"
            @flow-update-confirm="flowUpdateConfirm"
         />

         <!-- Resume flow -->
         <ResumeFlow
            v-else-if="state.resumeFlow.isVisible"
            :userBlockIdHistory="state.resumeFlow.userBlockIdHistory"
            :is-private-flow="isPrivateFlow"
            :participationResetConfirmed="state.participationResetConfirmed"
            @go-to-first-block="goToFirstBlock"
            @go-to-last-block="goToLastBlock"
            @update-user-block-id-history="updateUserBlockIdHistory"
            @public-resume-show-participation-reset-message="state.showParticipationResetMessage = true"
         />

         <!-- The Flowsel -->
         <!-- css tip: stretch to fill the remaining space of the page -->
         <div v-else class="container flex max-w-xl flex-1 items-center justify-center overflow-y-hidden">
            <TheFlowsel
               :blocks="blocks"
               :flow="state.flow"
               :currentBlockIndex="state.currentBlockIndex"
               :userBlockAnswer="userBlockAnswer"
               @answer-submitted="answerSubmitted"
               @participation-reset="inFlowParticipationReset"
               @previous-click="prevButtonClicked"
            />
         </div>
      </div>
   </PageLoader>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import { Head } from "@unhead/vue/components";
import { useFlowParticipationStore } from "@/stores/flowParticipationStore";
import FlowselNav from "@/components/flowsel/FlowselNav.vue";
import FlowsApi, { type FlowData, type FlowStudy } from "@/data/api-layer/flowsApi";
import { Theme } from "@/types";
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { AxiosError } from "axios";
import AnswersAPI, { type AnswerResp } from "@/data/api-layer/answersApi";
import { useAuthStore } from "@/stores/authStore";

const $route = useRoute();
const { t: $t } = useI18n();
const authStore = useAuthStore();
const flowParticipationStore = useFlowParticipationStore();

// Data
// ------------------------------
interface ResumeFlow {
   isVisible: boolean;
   userBlockIdHistory: string[];
}

interface UserBlockIndexHistory {
   blockId: string;
   blockIndex: number;
   blockVersionId: string;
   blockHeading: string;
}

interface State {
   isPageLoading: boolean;
   flow: FlowData;
   currentBlockIndex: number;
   userBlockIndexHistory: UserBlockIndexHistory[];
   currentAnswer: any; // Replace 'any' with a specific answer type
   resumeFlow: ResumeFlow;
   showParticipationResetMessage: boolean;
   participationResetConfirmed: boolean;
}

const loadErrors = ref<Error[]>([]);

const state = reactive<State>({
   isPageLoading: true,
   flow: null,
   currentBlockIndex: 0,
   userBlockIndexHistory: [],
   currentAnswer: null,
   resumeFlow: {
      isVisible: false,
      userBlockIdHistory: [],
   },
   showParticipationResetMessage: false,
   participationResetConfirmed: false,
});

const answersResp = ref<AnswerResp[]>([]);

// Watch
// ------------------------------
// watch for changes to the route param id - i.e. when we are going from flow/123 to flow/456
watch(
   () => $route.params.id,
   () => {
      // reset all the state variables if necessary
      resetFlowState();
      // fetch the new data
      getPageData();
   }
);

// Computed
// ------------------------------
const flowId = computed(() => {
   return $route.params?.id as string;
});

// users EXISTING answer for the currently viewed block
const userBlockAnswer = computed<AnswerResp | null>(() => {
   if (!currentBlock.value) return null;
   const blockAnswer = answersResp.value.find((answer) => answer?.blockId === currentBlock.value.id);

   return blockAnswer ? blockAnswer : null;
});

const study = computed<FlowStudy>(() => (state.flow ? state.flow.study : null));
const flowName = computed(() => (state.flow ? state.flow.title : null));
const blocks = computed<FlowselBlock[]>(() => {
   if (!state.flow?.blocks) return null;

   const flowselBlocks: FlowselBlock[] = state.flow.blocks.map((block) => {
      return {
         ...block,
         nextBlockIndex: null,
         nextBlockId: null,
      };
   });

   // add End block to the list of blocks
   addEndBlock(flowselBlocks, state.flow.endSection);

   // connect the natural order of blocks (the order they are presented to the user assuming there is no screenout or JumpTo from the BE)
   connectBlocks(flowselBlocks, state.flow.blockSections);

   return flowselBlocks;
});

// helpful for debugging
// const previousBlock = computed<Block>(() => {
//    if (state.userBlockIndexHistory.length === 0) return null;
//    const lastHistory = state.userBlockIndexHistory[state.userBlockIndexHistory.length - 1].blockIndex;
//    return blocks.value[lastHistory];
// });

// current block visible to the user
const currentBlock = computed<FlowselBlock>(() => {
   if (!blocks.value) return null;

   // find the block in the block list
   return blocks.value[state.currentBlockIndex];
});

const isFlowComplete = computed(() => {
   // flowParticipationData might not be available for public flows
   return flowParticipationStore.flowParticipationData?.reachedEndAt;
});

// private flows include isPublic=False or (isPublic=True and loggedIn and anonymousSubmissionsOnly=False)
const isPrivateFlow = computed(
   () => !state.flow.isPublic || (authStore.authTokenExists && !state.flow.anonymousSubmissionsOnly)
);

// page data requests
// https://miro.com/app/board/uXjVMIOIfrc=/ (See 'Technical implementation' board and 'Handling flow updates...' board)
// -----------------------------------------
async function getPageData() {
   // id doesn't exist in the params
   if (!flowId.value) {
      state.isPageLoading = false;
      loadErrors.value = [];
      return;
   }

   // delete all existing answers
   answersResp.value = [];

   try {
      // logic for flow access is handled in the navigation guard so we can assume the user has access to the flow
      const response: any = await FlowsApi.getFlowById(flowId.value).catch((error) => {
         throw error;
      });

      state.flow = response?.data?.data as FlowData;

      // handle private flow (the flow data is stored against the logged-in user)
      if (isPrivateFlow.value) {
         /*
            get a user flow / route guard should have created a participation
            include the create_if_not_found param if the flowId is "coreprofile" as the route guard will
            is not hit for "coreprofile" flow (due to the complex routing) and this creates the flow participation when hit
         */
         if (flowId.value === "coreprofile") {
            // Set the flow participation object in the store, as this will be used on the FlowselPage and it's child components
            await flowParticipationStore.setFlowParticipation("getOrCreateParticipation", flowId.value);
         } else {
            // Set the flow participation object in the store, as this will be used on the FlowselPage and it's child components
            await flowParticipationStore.setFlowParticipation("getParticipation", flowId.value);
         }
         const { resumeHistory } = flowParticipationStore.flowParticipation;

         // if there is previous history, show the resume page and set the block IDs
         if (resumeHistory?.length > 0) {
            state.resumeFlow.isVisible = true;
            state.resumeFlow.userBlockIdHistory = resumeHistory;

            // NOTE: if show_participation_reset_message is true, no resumeHistory is returned
         } else if (flowParticipationStore.flowParticipationData?.showParticipationResetMessage === true) {
            state.showParticipationResetMessage = true;
         }

         // handle anonymous public flow without resume
      } else if (state.flow.isPublic && !state.flow.allowPublicResume) {
         console.info("anonymous public flow without resume");

         // start a new public flow
         // and set the flow participation object in the store, as this will be used on the FlowselPage and it's child components
         await flowParticipationStore.setFlowParticipation("startPublicFlow", flowId.value);

         // handle anonymous public flow with resume
      } else {
         console.info("anonymous public flow with resume");

         // show the resume public flow component (this component handles flow-reset situations for public flows with resume)
         state.resumeFlow.isVisible = true;
      }
      loadErrors.value = [];
   } catch (e: any) {
      console.error("Error fetching survey data: ", e);
      loadErrors.value = [e as AxiosError];
   } finally {
      state.isPageLoading = false;
   }
}
// Mounted
// ------------------------------
onMounted(() => {
   // get the page data
   getPageData();
});

// Methods
// ------------------------------
function resetFlowState() {
   state.isPageLoading = true;
   state.currentBlockIndex = 0;
   state.userBlockIndexHistory = [];
   state.currentAnswer = null;
   state.resumeFlow.isVisible = false;
   state.resumeFlow.userBlockIdHistory = [];
   flowParticipationStore.$reset();
}

/** get the first block given a section ID
 * @param {String} sectionId the section ID to find the first block for
 */
function getFirstBlockIdInSection(sectionId: string) {
   const section = state.flow.blockSections.find((section) => section.id === sectionId);
   if (!section) {
      console.error(`Failed finding section with id: ${sectionId}`);
      return null;
   }

   return section.blocks[0];
}

/** To each block, add the next connecting block index so we know where to go next
 * @param {*} flowBlocks array of blocks
 * @param {Array} blockSections ex. [ {"id": "..", "label": "..", "blocks": [ "block_id"], "gotoSectionId": null}, ... ]
 */
function connectBlocks(flowBlocks: FlowselBlock[], blockSections) {
   // Step 1: Assign the first block and move it to the beginning of the array
   // -----------------------------------------

   const firstSectionBlockId = blockSections[0].blocks[0];
   const blockIndex = flowBlocks.findIndex((block) => block.id === firstSectionBlockId);

   if (blockIndex === -1) {
      console.error(`No block found for id ${firstSectionBlockId}.`);
      return;
   }
   const block = flowBlocks[blockIndex];
   block.isFirstBlock = true;
   // remove the item from this position
   flowBlocks.splice(blockIndex, 1);
   // add the item to the 1st position
   flowBlocks.unshift(block);

   // Step 2: Loop through each block section and figure out where each block should go Next
   // -----------------------------------------
   blockSections.forEach((section, sectionIndex) => {
      section.blocks.forEach((sectionBlockId, sectionBlockIndex) => {
         const block = flowBlocks.find((block) => block.id === sectionBlockId);
         if (!block) {
            console.error("Block not found: ", sectionBlockId, "In section: ", section.id);
         }

         // if this section block is not the last block in the array, the next section block ID is where the flow block should go to next
         if (sectionBlockIndex < section.blocks.length - 1) {
            // find the block in the flowBlocks array (this is the block we want to assign the next block to)
            // get the next section block ID
            const nextSectionBlockId = section.blocks[sectionBlockIndex + 1];
            // assign the next block index
            block.nextBlockIndex = flowBlocks.findIndex((block) => block.id === nextSectionBlockId);
            block.nextBlockId = nextSectionBlockId;
         }
         // last block ID in this section > point to the first block id in the next section
         else {
            // there is a next section ID but it is the END so the next block is one with ID: end-block)
            if (section.gotoSectionId && section.gotoSectionId === "ENDING") {
               block.nextBlockIndex = flowBlocks.findIndex((block) => block.id === "end-block");
               block.nextBlockId = "end-block";
            }
            // there is a next section ID specified > point to the first block of that section
            else if (section.gotoSectionId) {
               const nextSectionById = blockSections.find((next_section) => next_section.id === section.gotoSectionId);
               if (!nextSectionById) console.error("Section ID to JumpTo is not found: ", section.gotoSectionId);
               const nextSectionBlockId = nextSectionById.blocks[0];
               block.nextBlockIndex = flowBlocks.findIndex((block) => block.id === nextSectionBlockId);
               block.nextBlockId = nextSectionBlockId;
            }
            // next section is NULL and this is the last section > point to the end block
            else if (!section.gotoSectionId && sectionIndex === blockSections.length - 1) {
               block.nextBlockIndex = flowBlocks.findIndex((block) => block.id === "end-block");
               block.nextBlockId = "end-block";
            }
            // next section is NULL but this is not the last section > point to the first block id in the next section
            else {
               // get the next section's first block ID
               const nextSectionBlockId = blockSections[sectionIndex + 1].blocks[0];
               block.nextBlockIndex = flowBlocks.findIndex((block) => block.id === nextSectionBlockId);
               block.nextBlockId = nextSectionBlockId;
            }
         }
      });
   });
}

/** Add the End block to the list of blocks
 * @param {Array} blocks array of blocks
 */
function addEndBlock(blocks: FlowselBlock[], endSection) {
   const endBlock: FlowselBlock = {
      id: "end-block",
      componentName: "flow-end",
      data: endSection,
      flowId: flowId.value,

      // not important for the end block but we add for type consistency
      type: null,
      heading: null,
      blockVersionId: null,
      createdAt: null,
      updatedAt: null,
      subheading: null,

      isRequired: true,
      versionDiff: null,
      jumpTo: null,
      screenoutImmediately: null,
      flowVersionId: null,
      localeId: null,

      nextBlockId: null,
      nextBlockIndex: null,
      isFirstBlock: false,
   };

   return blocks.push(endBlock);
}

/** When 'next' has been clicked on a block component and an answer has been successfully submitted
 * Note - this happens even on 'information' blocks - in that the case the answer is { data: null }
 * @param {object} response the response sent back from AnswersApi.submit()
 */
function answerSubmitted(response) {
   // participationResetConfirmed should be reset as soon as an answer is submitted, to allow users to resume their new, updated flow
   state.participationResetConfirmed = false;

   // Update state with the answer from the db
   state.currentAnswer = response.data.data;

   // Check if the answer already exists in the answersResp array, if it does, update it, otherwise add it
   const blockIndex = answersResp.value.findIndex((answer) => answer.blockId === response.data.blockId);
   if (blockIndex >= 0) {
      answersResp.value[blockIndex] = response.data;
   } else {
      answersResp.value.push(response.data);
   }

   const jumpToSectionId = response.jumpTo?.goToSectionId;
   const screenOut = response.screenout;

   console.info(
      currentBlock.value.id,
      state.currentBlockIndex,
      currentBlock.value.blockVersionId,
      currentBlock.value.heading
   );
   // log the previous block id in the block index history

   state.userBlockIndexHistory.push({
      blockId: currentBlock.value.id,
      blockIndex: state.currentBlockIndex,
      blockVersionId: currentBlock.value.blockVersionId,
      blockHeading: currentBlock.value.heading,
   });

   // if the BE returns a response with screenout=true, we go to the END block
   if (screenOut) {
      console.log("Screenout received - going to the END block");
      state.currentBlockIndex = blocks.value.length - 1;
      state.currentAnswer = null;
   }
   // no screenout, do we have jump_to from the BE?
   else {
      // if the BE returns a response with a jump_to which is NOT NULL, we go to the first block of that section instead of the default next block
      if (jumpToSectionId && jumpToSectionId !== "ENDING") {
         const jumpToBlockId = getFirstBlockIdInSection(jumpToSectionId);
         const jumpToBlockIndex = blocks.value.findIndex((block) => block.id === jumpToBlockId);
         if (jumpToBlockIndex === -1) {
            console.error("BE requested to Jump To Block ID which is not found: ", jumpToBlockId);
            return;
         }
         console.log("jumpToBlockIndex", jumpToBlockIndex);
         state.currentBlockIndex = jumpToBlockIndex;
         console.log("BE requested to Jump To block ID: ", jumpToBlockId, "index: ", state.currentBlockIndex);
      }
      // BE returned response to jump_to which is ENDING, we go to the end block
      else if (jumpToSectionId && jumpToSectionId === "ENDING") {
         state.currentBlockIndex = blocks.value.findIndex((block) => block.id === "end-block");
         console.log("BE requested to Jump To END block");
      }
      // The BE didnt return a response with a jump_to so we use the default next block
      else {
         console.log(
            "Going to default next block, ID: ",
            currentBlock.value.nextBlockId,
            "index: ",
            currentBlock.value.nextBlockIndex
         );
         state.currentBlockIndex = currentBlock.value.nextBlockIndex;
      }

      // If the user presses Next and there was already an existing answer for this block, set the current answer to the users DB stored answer
      state.currentAnswer = userBlockAnswer.value?.data; // if there is an existing answer, only use data (ignore metadata)
   }
}

function updateUserBlockIdHistory(newHistory) {
   state.resumeFlow.userBlockIdHistory = newHistory;
}

/** Previous was clicked, we allow the user to go back regardless of input being invalid */
function prevButtonClicked() {
   // go back to the previous block and remove from the index history
   const prevBlockIndex = state.userBlockIndexHistory.pop();

   // set the currentBlockIndex which will trigger the carousel to go back to this block
   state.currentBlockIndex = prevBlockIndex.blockIndex;

   // set the current answer to the users DB stored block answer
   state.currentAnswer = userBlockAnswer.value.data;
}

// Go to the last visited block
async function goToLastBlock(): Promise<void> {
   // fetch answers for the current flow
   await fetchAnswers();

   if (state.resumeFlow.userBlockIdHistory.length < 1) {
      console.error("No block ID history found to resume flow from");
   }

   // gets the last block index to use for the currentBlockIndex
   const lastBlockId = state.resumeFlow.userBlockIdHistory[state.resumeFlow.userBlockIdHistory.length - 1];
   const lastBlockIndex = blocks.value.findIndex((block) => block.id === lastBlockId);

   if (lastBlockIndex < 0) {
      console.error("Failed to find last block index for block ID: ", lastBlockId);
   }

   // set the userBlockHistory based on the users stored resume block ID history (exclude the last block which is the current block)
   for (let i = 0; i < state.resumeFlow.userBlockIdHistory.length - 1; i++) {
      const blockId = state.resumeFlow.userBlockIdHistory[i];
      const blockIndex = blocks.value.findIndex((block) => block.id === blockId);
      if (blockIndex >= 0) {
         state.userBlockIndexHistory.push({
            blockId,
            blockIndex: blockIndex,
            blockVersionId: blocks.value[blockIndex].blockVersionId,
            blockHeading: blocks.value[blockIndex].heading,
         });
      }
   }

   // update current block index
   state.currentBlockIndex = lastBlockIndex;

   // update current answer
   state.currentAnswer = userBlockAnswer.value?.data;

   // hide resume
   state.resumeFlow.isVisible = false;
}

// Called if the user is filling out a flow, the flow is updated while they are doing this, and then they try to submit an answer
async function inFlowParticipationReset() {
   // In this case, we'll reset the flow data. For private flows, FlowParticipationsApi.getParticipation() will be called again immediately,
   // and the response from the BE will include show_participation_reset_message: true, and so the 'flow updated' message will be displayed
   resetFlowState();
   await getPageData();

   // However for flows with no resume functionality, or where you need to enter a code to resume (public-resume),
   // the 'flow updated' message needs to be shown at this point, otherwise the user will have to enter more information before they see an explanation
   if (!isPrivateFlow.value) state.showParticipationResetMessage = true;
}

// Called when the user clicks "ok" on the "flow updated" (participation reset) message
async function flowUpdateConfirm() {
   // For a private flow, go to the first block.
   // On a public flow, a new submission needs to be started, and this is handled within the public resume block, which will be showing already
   if (isPrivateFlow.value) {
      await goToFirstBlock();
   }

   state.participationResetConfirmed = true;
   state.showParticipationResetMessage = false;
}

// Go to the first block
async function goToFirstBlock(isNewFlow = false): Promise<void> {
   // if the flow is not newly created, fetch all previous answers
   if (!isNewFlow) {
      await fetchAnswers();
   }

   // update current block index
   state.currentBlockIndex = 0;

   // update current answer
   state.currentAnswer = userBlockAnswer.value?.data;

   // hide resume & 'flow updated' information
   state.resumeFlow.isVisible = false;
   state.showParticipationResetMessage = false;
}

async function fetchAnswers() {
   // There are 2 separate requests for getting answers and they depend on the presence of token
   // Token is used to authorise users in anonymous public flows
   // This logic (to determine if token is present) can't be a part of the service layer, because we want to keep it service generic so it can be auto-generated by OpenAPI in the future
   if (flowParticipationStore.token) {
      answersResp.value = await AnswersAPI.getAllWithToken(flowId.value, flowParticipationStore.token);
   } else {
      answersResp.value = await AnswersAPI.getAll(flowId.value);
   }
}
</script>
